import { debugWarn, InputFieldType, Measure, Nullable } from '@e2m-customer-portal/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LimitSpecification } from '../../generated/graphql';

const TRANSLATION_MAP: Readonly<
  Partial<
    Record<InputFieldType, Readonly<Record<'min' | 'max', Readonly<Partial<Record<LimitSpecification, string>>>>>>
  >
> = {
  [InputFieldType.PRODUCTION]: {
    // does not have contractual limits
    min: {
      [LimitSpecification.TECHNICAL]: 'PRODUCTION_LESS_THAN_ZERO', // PROD_13
    },
    max: {
      [LimitSpecification.TECHNICAL]: 'PRODUCTION_GREATER_THAN_INSTALLED_POWER', // PROD_14
      [LimitSpecification.INSTALLED_CAPACITY]: 'PRODUCTION_GREATER_THAN_INSTALLED_POWER', // PROD_12
    },
  },
  [InputFieldType.FCR]: {
    min: {
      [LimitSpecification.CONTRACTUAL]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_PRODUCTION_AND_CONTRACTUAL_MINIMUM', // FCR_05
      [LimitSpecification.TECHNICAL]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_PRODUCTION_AND_CONTROLLABLE_MINIMUM', // FCR_06
      [LimitSpecification.TEMPLATE]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_PRODUCTION_AND_TEMPLATE_MINIMUM', // FCR_11
    },
    max: {
      [LimitSpecification.FCR]: 'FCR_GREATER_THAN_FLEX_BAND_MINIMUM', // FCR_01
      [LimitSpecification.CONTRACTUAL]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_CONTRACTUAL_MAXIMUM_AND_PRODUCTION', // FCR_03
      [LimitSpecification.TECHNICAL]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_CONTROLLABLE_MAXIMUM_AND_PRODUCTION', // FCR_04
      [LimitSpecification.TEMPLATE]: 'FCR_GREATER_THAN_DIFFERENCE_BETWEEN_TEMPLATE_MAXIMUM_AND_PRODUCTION', // FCR_10
    },
  },
  [InputFieldType.AFRR_LOWER_LIMIT]: {
    min: {
      [LimitSpecification.CONTRACTUAL]: 'AFRR_MINIMUM_LESS_THAN_CONTRACTUAL_MINIMUM', // AFRR_MIN_01
      [LimitSpecification.TECHNICAL]: 'AFRR_MINIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // AFRR_MIN_02
      [LimitSpecification.TEMPLATE]: 'AFRR_MINIMUM_LESS_THAN_TEMPLATE_MINIMUM', // AFRR_MIN_03
    },
    max: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'AFRR_MINIMUM_GREATER_THAN_AFRR_MAXIMUM', // AFRR_MIN_04
      [LimitSpecification.CONTRACTUAL]: 'AFRR_MINIMUM_GREATER_THAN_CONTRACTUAL_MAXIMUM', // AFRR_MIN_05
      [LimitSpecification.TECHNICAL]: 'AFRR_MINIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // AFRR_MIN_06
      [LimitSpecification.TEMPLATE]: 'AFRR_MINIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // AFRR_MIN_07
    },
  },
  [InputFieldType.AFRR_UPPER_LIMIT]: {
    min: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'AFRR_MAXIMUM_LESS_THAN_AFRR_MINIMUM', // AFRR_MAX_05
      [LimitSpecification.CONTRACTUAL]: 'AFRR_MAXIMUM_LESS_THAN_CONTRACTUAL_MINIMUM', // AFRR_MAX_06
      [LimitSpecification.TECHNICAL]: 'AFRR_MAXIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // AFRR_MAX_07
      [LimitSpecification.TEMPLATE]: 'AFRR_MAXIMUM_LESS_THAN_TEMPLATE_MINIMUM', // AFRR_MAX_08
    },
    max: {
      [LimitSpecification.CONTRACTUAL]: 'AFRR_MAXIMUM_GREATER_THAN_CONTRACTUAL_MAXIMUM', // AFRR_MAX_01
      [LimitSpecification.TECHNICAL]: 'AFRR_MAXIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // AFRR_MAX_02
      [LimitSpecification.TEMPLATE]: 'AFRR_MAXIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // AFRR_MAX_03
    },
  },
  [InputFieldType.MFRR_LOWER_LIMIT]: {
    min: {
      [LimitSpecification.CONTRACTUAL]: 'MFRR_MINIMUM_LESS_THAN_CONTRACTUAL_MINIMUM', // MFRR_MIN_01
      [LimitSpecification.TECHNICAL]: 'MFRR_MINIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // MFRR_MIN_02
      [LimitSpecification.TEMPLATE]: 'MFRR_MINIMUM_LESS_THAN_TEMPLATE_MINIMUM', // MFRR_MIN_03
    },
    max: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'MFRR_MINIMUM_GREATER_THAN_MFRR_MAXIMUM', // MFRR_MIN_04
      [LimitSpecification.CONTRACTUAL]: 'MFRR_MINIMUM_GREATER_THAN_CONTRACTUAL_MAXIMUM', // MFRR_MIN_05
      [LimitSpecification.TECHNICAL]: 'MFRR_MINIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // MFRR_MIN_06
      [LimitSpecification.TEMPLATE]: 'MFRR_MINIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // MFRR_MIN_07
    },
  },
  [InputFieldType.MFRR_UPPER_LIMIT]: {
    min: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'MFRR_MAXIMUM_LESS_THAN_MFRR_MINIMUM', // MFRR_MAX_05
      [LimitSpecification.CONTRACTUAL]: 'MFRR_MAXIMUM_LESS_THAN_CONTRACTUAL_MINIMUM', // MFRR_MAX_06
      [LimitSpecification.TECHNICAL]: 'MFRR_MAXIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // MFRR_MAX_07
      [LimitSpecification.TEMPLATE]: 'MFRR_MAXIMUM_LESS_THAN_TEMPLATE_MINIMUM', // MFRR_MAX_08
    },
    max: {
      [LimitSpecification.CONTRACTUAL]: 'MFRR_MAXIMUM_GREATER_THAN_CONTRACTUAL_MAXIMUM', // MFRR_MAX_01
      [LimitSpecification.TECHNICAL]: 'MFRR_MAXIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // MFRR_MAX_02
      [LimitSpecification.TEMPLATE]: 'MFRR_MAXIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // MFRR_MAX_03
    },
  },
  [InputFieldType.FLEX_PLUS_LOWER_LIMIT]: {
    min: {
      [LimitSpecification.TECHNICAL]: 'FLEX_PLUS_MINIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // FLEX_MIN_02
      [LimitSpecification.TEMPLATE]: 'FLEX_PLUS_MINIMUM_LESS_THAN_TEMPLATE_MINIMUM', // FLEX_MIN_03
    },
    max: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'FLEX_PLUS_MINIMUM_GREATER_THAN_FLEX_PLUS_MAXIMUM', // FLEX_MIN_04
      [LimitSpecification.TECHNICAL]: 'FLEX_PLUS_MINIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // FLEX_MIN_06
      [LimitSpecification.TEMPLATE]: 'FLEX_PLUS_MINIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // FLEX_MIN_07
    },
  },
  [InputFieldType.FLEX_PLUS_UPPER_LIMIT]: {
    min: {
      [LimitSpecification.PAIRED_MAXIMUM]: 'FLEX_PLUS_MAXIMUM_LESS_THAN_FLEX_PLUS_MINIMUM', // FLEX_MAX_05
      [LimitSpecification.TECHNICAL]: 'FLEX_PLUS_MAXIMUM_LESS_THAN_CONTROLLABLE_MINIMUM', // FLEX_MAX_07
      [LimitSpecification.TEMPLATE]: 'FLEX_PLUS_MAXIMUM_LESS_THAN_TEMPLATE_MINIMUM', // FLEX_MAX_08
    },
    max: {
      [LimitSpecification.TECHNICAL]: 'FLEX_PLUS_MAXIMUM_GREATER_THAN_CONTROLLABLE_MAXIMUM', // FLEX_MAX_02
      [LimitSpecification.TEMPLATE]: 'FLEX_PLUS_MAXIMUM_GREATER_THAN_TEMPLATE_MAXIMUM', // FLEX_MAX_03
    },
  },
};

interface UseInputFieldErrorTextResult {
  /**
   * Returns a helper text that displays when the input value is more than the maximum value.
   * @param value
   * @param field
   * @param reason
   * @param fcrVariant
   */
  getMaxErrorText: (
    value: Measure,
    field: InputFieldType,
    reason: LimitSpecification,
    fcrVariant?: Nullable<'sub'>,
  ) => string;

  /**
   * Returns a helper text that displays when the input value is less than the minimum value.
   * @param value
   * @param field
   * @param reason
   */
  getMinErrorText: (value: Measure, field: InputFieldType, reason: LimitSpecification) => string;
}

export function useInputFieldErrorText(): UseInputFieldErrorTextResult {
  const [t] = useTranslation('validation');

  return useMemo(
    () => ({
      getMaxErrorText: (value, field, reason) => {
        let key = TRANSLATION_MAP[field]?.max[reason];
        if (!key) {
          key = 'COMMON_MUST_BE_LESS_OR_EQUAL';
          debugWarn(
            'No specialized error message: Dir=Max Value=%s Type=%s Reason=%s Variant=%s',
            `${value.value}${value.unit}`,
            field,
            reason,
          );
        }

        return t(key, { value, unit: 'kW' });
      },
      getMinErrorText: (value, field, reason) => {
        let key = TRANSLATION_MAP[field]?.min[reason];
        if (!key) {
          key = 'COMMON_MUST_BE_GREATER_OR_EQUAL';
          debugWarn(
            'No specialized error message: Dir=Min Value=%s Type=%s Reason=%s',
            `${value.value}${value.unit}`,
            field,
            reason,
          );
        }

        return t(key, { value, unit: 'kW' });
      },
    }),
    [t],
  );
}
